import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logActivity } from "@/lib/activity-log";
import type { DeviceHashes } from "@/hooks/useDeviceHashes";

export interface HomeworkSubtask {
  id: string;
  homework_id: string;
  title: string;
  completed: boolean;
  completed_at: string | null;
  requires_upload: "image" | "video" | "audio" | null;
  estimated_minutes: number | null;
  sort_order: number;
}

export interface Homework {
  id: string;
  child_id: string;
  family_id: string;
  week_number: number;
  subject: string | null;
  title: string;
  due_date: string | null;
  scope: "week_due" | "daily" | "flexible_week";
  confidence: "low" | "medium" | "high" | null;
  source_fingerprint: string;
  import_batch_id: string | null;
  created_by: string | null;
  created_at: string;
  completed_at: string | null;
  original_text: string | null;
  homework_subtasks: HomeworkSubtask[];
}

export function useHomework(childId: string | undefined, weekNumber?: number, deviceHashes?: DeviceHashes | null) {
  const queryClient = useQueryClient();
  const isDevice = !!deviceHashes;

  const queryKey = ["homework", childId, weekNumber, isDevice ? "device" : "parent"];

  const { data: items = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!childId) return [];

      if (isDevice) {
        // Fetch homework via RPC
        const { data: hwData, error: hwError } = await supabase.rpc("get_homework_for_device", {
          _device_id_hash: deviceHashes.deviceIdHash,
          _session_token_hash: deviceHashes.sessionTokenHash,
          _week_number: weekNumber ?? null,
        });
        if (hwError) throw hwError;
        if (!hwData || hwData.length === 0) return [];

        // Fetch subtasks
        const hwIds = (hwData as any[]).map((h: any) => h.id);
        const { data: stData, error: stError } = await supabase.rpc("get_homework_subtasks_for_device", {
          _device_id_hash: deviceHashes.deviceIdHash,
          _session_token_hash: deviceHashes.sessionTokenHash,
          _homework_ids: hwIds,
        });
        if (stError) throw stError;

        // Group subtasks by homework_id
        const subtaskMap = new Map<string, any[]>();
        for (const st of (stData ?? []) as any[]) {
          if (!subtaskMap.has(st.homework_id)) subtaskMap.set(st.homework_id, []);
          subtaskMap.get(st.homework_id)!.push(st);
        }

        return (hwData as any[]).map((hw: any) => ({
          ...hw,
          scope: hw.scope as Homework["scope"],
          confidence: hw.confidence as Homework["confidence"],
          homework_subtasks: (subtaskMap.get(hw.id) || []).sort(
            (a: any, b: any) => a.sort_order - b.sort_order
          ),
        })) as Homework[];
      }

      // Parent/auth path
      let query = supabase
        .from("homework" as any)
        .select("*, homework_subtasks(*)")
        .eq("child_id", childId)
        .order("created_at", { ascending: true });
      if (weekNumber !== undefined) {
        query = query.eq("week_number", weekNumber);
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data as any[]).map((hw) => ({
        ...hw,
        homework_subtasks: (hw.homework_subtasks || []).sort(
          (a: any, b: any) => a.sort_order - b.sort_order
        ),
      })) as Homework[];
    },
    enabled: !!childId && (!isDevice || !!deviceHashes),
    refetchInterval: isDevice ? 5000 : undefined,
  });

  // Realtime subscription (parent only)
  useEffect(() => {
    if (!childId || isDevice) return;
    const channel = supabase
      .channel(`homework-${childId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "homework", filter: `child_id=eq.${childId}` },
        () => queryClient.invalidateQueries({ queryKey: ["homework", childId] })
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "homework_subtasks" },
        () => queryClient.invalidateQueries({ queryKey: ["homework", childId] })
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [childId, queryClient, isDevice]);

  const toggleSubtask = useMutation({
    mutationFn: async ({ subtaskId, completed }: { subtaskId: string; completed: boolean }) => {
      if (isDevice) {
        const { error } = await supabase.rpc("toggle_homework_subtask_for_device", {
          _device_id_hash: deviceHashes!.deviceIdHash,
          _session_token_hash: deviceHashes!.sessionTokenHash,
          _subtask_id: subtaskId,
          _completed: completed,
        });
        if (error) throw error;
        return;
      }

      // Parent/auth path
      const { error } = await supabase
        .from("homework_subtasks" as any)
        .update({
          completed,
          completed_at: completed ? new Date().toISOString() : null,
        } as any)
        .eq("id", subtaskId);
      if (error) throw error;

      const subtask = items
        .flatMap((hw) => hw.homework_subtasks.map((st) => ({ ...st, homework_id: hw.id })))
        .find((st) => st.id === subtaskId);
      if (subtask && completed) {
        const hw = items.find((h) => h.id === subtask.homework_id);
        if (hw) {
          const allDone = hw.homework_subtasks.every(
            (st) => st.id === subtaskId ? true : st.completed
          );
          if (allDone) {
            await supabase
              .from("homework" as any)
              .update({ completed_at: new Date().toISOString() } as any)
              .eq("id", hw.id);
          }
        }
      }
      if (!completed && subtask) {
        await supabase
          .from("homework" as any)
          .update({ completed_at: null } as any)
          .eq("id", subtask.homework_id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homework", childId] });
    },
    onError: () => {
      toast.error("Kunne ikke oppdatere deloppgave");
    },
  });

  const deleteHomework = useMutation({
    mutationFn: async (homeworkId: string) => {
      const hw = items.find((h) => h.id === homeworkId);
      const { error } = await supabase
        .from("homework" as any)
        .delete()
        .eq("id", homeworkId);
      if (error) throw error;
      if (hw) {
        logActivity({ familyId: hw.family_id, childId: hw.child_id, action: "deleted", entityType: "homework", entityId: homeworkId, changes: { title: { old: hw.title, new: null } } });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homework", childId] });
    },
    onError: () => {
      toast.error("Kunne ikke slette lekse");
    },
  });

  const archiveHomework = useMutation({
    mutationFn: async (homeworkId: string) => {
      const hw = items.find((h) => h.id === homeworkId);
      const { error } = await supabase
        .from("homework" as any)
        .update({ completed_at: new Date().toISOString() } as any)
        .eq("id", homeworkId);
      if (error) throw error;
      if (hw) {
        logActivity({ familyId: hw.family_id, childId: hw.child_id, action: "completed", entityType: "homework", entityId: homeworkId });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homework", childId] });
      toast.success("Lekse fullført");
    },
    onError: () => {
      toast.error("Kunne ikke fullføre lekse");
    },
  });

  const unarchiveHomework = useMutation({
    mutationFn: async (homeworkId: string) => {
      const hw = items.find((h) => h.id === homeworkId);
      const { error } = await supabase
        .from("homework" as any)
        .update({ completed_at: null } as any)
        .eq("id", homeworkId);
      if (error) throw error;
      if (hw) {
        logActivity({ familyId: hw.family_id, childId: hw.child_id, action: "uncompleted", entityType: "homework", entityId: homeworkId });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homework", childId] });
      toast.success("Lekse nullstilt");
    },
    onError: () => {
      toast.error("Kunne ikke nullstille lekse");
    },
  });

  const completionStats = items.map((hw) => {
    const total = hw.homework_subtasks.length;
    const done = hw.homework_subtasks.filter((st) => st.completed).length;
    return { homeworkId: hw.id, total, done, percentage: total > 0 ? Math.round((done / total) * 100) : 0 };
  });

  return { items, isLoading, toggleSubtask, deleteHomework, archiveHomework, unarchiveHomework, completionStats };
}

export function useHomeworkByWeek(childId: string | undefined) {
  return useQuery({
    queryKey: ["homework-weeks", childId],
    queryFn: async () => {
      if (!childId) return [];
      const { data, error } = await supabase
        .from("homework" as any)
        .select("week_number, import_batch_id, created_at")
        .eq("child_id", childId)
        .order("week_number", { ascending: false });
      if (error) throw error;
      const weeks = new Map<number, { count: number; batchId: string | null; importedAt: string }>();
      for (const row of data as any[]) {
        if (!weeks.has(row.week_number)) {
          weeks.set(row.week_number, { count: 0, batchId: row.import_batch_id, importedAt: row.created_at });
        }
        weeks.get(row.week_number)!.count++;
      }
      return Array.from(weeks.entries()).map(([weekNumber, info]) => ({
        weekNumber,
        ...info,
      }));
    },
    enabled: !!childId,
  });
}
